import { useQuery } from "@tanstack/react-query";

export type Portal = "CONSUMER" | "SELLER";

export type Faq = {
  id: string;
  title: string;
  content: string;
};

export type FaqCategory = {
  id: string;
  title: string;
  items: Faq[];
};

async function getFaqs(portal: Portal) {
  const resp = await fetch(`/api/faq?portal=${portal}`);
  return resp.json();
}

export function useFaqsBase(select: any, portal: Portal): any {
  return useQuery({
    queryKey: ["faq"],
    queryFn: () => getFaqs(portal),
    select,
  });
}

export function useFaqs(
  portal: Portal,
  filters: { categories: Array<string>; query: string } = {
    categories: [],
    query: "",
  },
): any {
  const { query, categories } = filters;
  const faqItemContainQuery = (item: Faq) =>
    item.title.toLowerCase().includes(query.toLowerCase()) ||
    item.content.toLowerCase().includes(query.toLowerCase());

  return useFaqsBase(
    (faqs: FaqCategory[]) =>
      faqs
        .filter((faq) => {
          return categories.length ? categories.includes(faq.id) : true;
        })
        .filter((faq) => {
          return faq.items.some(faqItemContainQuery);
        })
        .map((faq) => ({
          ...faq,
          items: faq.items.filter(faqItemContainQuery),
        })),
    portal,
  );
}

export function useFaq(
  portal: Portal,
  id: string,
): { data: { faq: Faq; category: FaqCategory } } {
  return useFaqsBase(
    (faqs: FaqCategory[]) => ({
      faq: faqs
        .map((fc: FaqCategory) => fc.items.find((f: any) => f.id == id))
        .find((fa: Faq | undefined) => fa !== undefined),
      category: faqs.find((fc: FaqCategory) =>
        fc.items.some((f: any) => f.id == id),
      ),
    }),
    portal,
  );
}
